/**
 * Como o editor desenha uma execução: cor, ícone e rótulo por status, e os
 * formatadores de data e duração.
 *
 * Saíram de `components/ExecutionHistorySidebar.tsx` (eram `const` privados do
 * arquivo) porque a modal "Visualizar contexto" mostra a MESMA lista de
 * execuções, a um clique de distância — duas cópias das mesmas palavras
 * envelheceriam em direções diferentes. Valores byte a byte iguais aos de
 * antes: esta extração não muda nada na tela.
 *
 * Por que não o `StatusPill` compartilhado: o mapa dele discorda deste em dois
 * status (`cancelled` vermelho × cinza; `suspended` cinza × âmbar) e o override
 * de tema claro do `Modal` não redefine os tokens que ele usa.
 */

// Status colors for execution states (semantic DS tokens)
export const EXECUTION_STATUS_COLORS = {
  pending: 'var(--theme-warning-color)',
  running: 'var(--theme-info-color)',
  completed: 'var(--theme-success-color)',
  failed: 'var(--theme-error-color)',
  cancelled: 'var(--theme-muted-text)',
  suspended: 'var(--theme-warning-color)',
} as const;

// Status icons for execution states
export const EXECUTION_STATUS_ICONS = {
  pending: 'clock',
  running: 'loader',
  completed: 'check-circle',
  failed: 'x-circle',
  cancelled: 'alert-circle',
  suspended: 'pause-circle',
} as const;

// Status labels in Portuguese
export const EXECUTION_STATUS_LABELS = {
  pending: 'Pendente',
  running: 'Em execução',
  completed: 'Concluído',
  failed: 'Falhou',
  cancelled: 'Cancelado',
  suspended: 'Aguardando retomada',
} as const;

/**
 * Formata a duração em milissegundos para uma string legível
 */
export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${minutes}m ${seconds}s`;
}

/**
 * Formata uma data ISO como hora relativa (mockup: "hoje 14:22")
 */
export function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const time = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === now.toDateString()) return `hoje ${time}`;
  if (date.toDateString() === yesterday.toDateString()) return `ontem ${time}`;
  const day = date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  return `${day} ${time}`;
}

/**
 * Data e hora completas (Tooltip da linha "Aguardando horário comercial" — o
 * texto da linha usa a forma relativa, "hoje 14:22").
 */
export function formatFullDateTime(isoString: string): string {
  return new Date(isoString).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
